import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

import type { ConfirmDiscardProps } from '@/shared/forms/types';

import { Button } from './Button';
import { Modal } from './Modal';

/**
 * FORM-04 — "Discard unsaved changes?" (UI/UX Pro Max "sheet-dismiss-confirm":
 * confirm before dismissing a sheet or modal with unsaved changes).
 *
 * A dialog of its own, opened over the form it protects: a native modal
 * dialog shown after the form's drawer or dialog sits above it in the top
 * layer, so it is reachable from any of them (R-050 stacking: dialogs 61 over
 * drawers 41). Keep editing is the safe answer — it takes focus, and Escape
 * gives it. Discard says what it does and nothing else.
 */
export function ConfirmDiscard({ open, onKeep, onDiscard, title, body }: ConfirmDiscardProps) {
  const { t } = useTranslation();
  const keep = useRef<HTMLButtonElement>(null);

  // After the dialog's own effect has shown it (a child's effect runs first).
  useEffect(() => {
    if (open) keep.current?.focus();
  }, [open]);

  return (
    <Modal
      open={open}
      title={title ?? t('shared.form.discard.title')}
      onClose={onKeep}
      footer={
        <>
          <Button ref={keep} variant="secondary" onClick={onKeep}>
            {t('shared.form.discard.keep')}
          </Button>
          <Button variant="danger" onClick={onDiscard}>
            {t('shared.form.discard.discard')}
          </Button>
        </>
      }
    >
      <p className="type-ui text-ink">{body ?? t('shared.form.discard.body')}</p>
    </Modal>
  );
}

/**
 * The router's half, mounted only under a data router (`useBlocker` throws
 * anywhere else). A change of path or query is held while there is unsaved
 * input; the person's answer proceeds or stays.
 */
export function NavigationBlocker({
  shouldBlock,
  ask,
}: {
  shouldBlock: () => boolean;
  ask: () => Promise<boolean>;
}) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search) &&
      shouldBlock(),
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    let current = true;
    void ask().then((leave) => {
      if (!current) return;
      if (leave) blocker.proceed();
      else blocker.reset();
    });

    return () => {
      current = false;
    };
  }, [blocker, ask]);

  return null;
}
